import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PATIENT_MESH_DPA_MARKDOWN } from "../../legal/patientMeshDpaAgreementText";
import { openDpaInNewTab } from "../../utils/openDpaWindow";

export default function PatientMeshDpaModal({ open, onClose, dpaDetails, darkMode = false }) {
  const formattedMarkdown = useMemo(() => {
    const details = dpaDetails || {};

    let md = PATIENT_MESH_DPA_MARKDOWN;
    md = md.replace(/{{DATA_CONTROLLER_TITLE_NAME}}/g, details.titleName || "-");
    md = md.replace(/{{AUTHORIZED_PERSON}}/g, details.authorizedPerson || "-");
    md = md.replace(/{{ACCEPTANCE_DATE}}/g, details.acceptanceDate || "-");
    md = md.replace(/{{SUBPROCESSOR_NAME}}/g, details.subprocessorName || "Google LLC / Cloud Provider");
    md = md.replace(/{{SERVICE_TYPE}}/g, details.serviceType || "Bulut Altyapı & Siber Güvenlik");
    md = md.replace(/{{PROCESSED_DATA}}/g, details.processedData || "Sistem / Oturum / Güvenlik Logları");
    md = md.replace(/{{HOSTING_COUNTRY}}/g, details.hostingCountry || "Türkiye / AB");
    md = md.replace(/{{TRANSFER_MECHANISM}}/g, details.transferMechanism || "Standart Sözleşme (KVKK m.9)");
    md = md.replace(/{{SECURITY_MEASURE}}/g, details.securityMeasure || "AES-256 / TLS 1.3 / ISO 27001");

    return md;
  }, [dpaDetails]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>PatientMesh Veri İşleme Sözleşmesi (DPA)</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; p-4; margin: 20px; color: #1e293b; line-height: 1.6; }
            h1 { font-size: 18pt; border-bottom: 2px solid #0f172a; padding-bottom: 8px; }
            h3 { font-size: 14pt; margin-top: 18px; color: #0f172a; }
            p, li { font-size: 10pt; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 9pt; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; vertical-align: top; }
            th { background-color: #f1f5f9; }
            hr { border: none; border-top: 1px solid #e2e8f0; margin: 15px 0; }
            @page { margin: 15mm; }
          </style>
        </head>
        <body>
          <div id="print-content"></div>
        </body>
      </html>
    `);

    // Render markdown content to string via HTML or append pre-formatted container
    const container = printWindow.document.getElementById("print-content");
    const dpaElement = document.getElementById("patientmesh-dpa-print-area");
    if (dpaElement && container) {
      container.innerHTML = dpaElement.innerHTML;
    }

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{
        zIndex: 1700,
      }}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: darkMode ? "#1A202C" : "#FFFFFF",
          color: darkMode ? "#EDF2F7" : "#2D3748",
          boxShadow: "0 25px 65px rgba(15, 23, 42, 0.25)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "#0F172A",
          color: "#FFFFFF",
          py: 2.2,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <VerifiedUserOutlinedIcon sx={{ color: "#38BDF8", fontSize: 26 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#FFFFFF" }}>
              Veri İşleme Sözleşmesi (DPA)
            </Typography>
            <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "0.75rem", display: "block" }}>
              PatientMesh Kişisel Verilerin İşlenmesi ve Aktarılmasına İlişkin Taahhütname
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: "#94A3B8",
            "&:hover": { color: "#FFFFFF", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: 3, bgcolor: darkMode ? "#1A202C" : "#F8FAFC" }}>
        <Paper
          id="patientmesh-dpa-print-area"
          elevation={0}
          sx={{
            p: 3,
            bgcolor: darkMode ? "#2D3748" : "#FFFFFF",
            border: `1px solid ${darkMode ? "#4A5568" : "#E2E8F0"}`,
            borderRadius: "12px",
            maxHeight: "60vh",
            overflowY: "auto",
            "& h1": { fontSize: "1.35rem", fontWeight: 700, color: darkMode ? "#F7FAFC" : "#0F172A", mb: 1 },
            "& h3": { fontSize: "1.05rem", fontWeight: 700, color: darkMode ? "#EDF2F7" : "#1E293B", mt: 2, mb: 1 },
            "& p": { fontSize: "0.88rem", color: darkMode ? "#CBD5E0" : "#334155", lineHeight: 1.6, mb: 1.5 },
            "& ul, & ol": { pl: 2.5, mb: 1.5 },
            "& li": { fontSize: "0.88rem", color: darkMode ? "#CBD5E0" : "#334155", mb: 0.5 },
            "& hr": { border: "none", borderTop: `1px solid ${darkMode ? "#4A5568" : "#E2E8F0"}`, my: 2 },
            "& table": {
              width: "100%",
              borderCollapse: "collapse",
              my: 2,
              fontSize: "0.85rem",
            },
            "& th": {
              bgcolor: darkMode ? "#1A202C" : "#F1F5F9",
              color: darkMode ? "#F7FAFC" : "#0F172A",
              fontWeight: 700,
              p: 1.2,
              textAlign: "left",
              border: `1px solid ${darkMode ? "#4A5568" : "#CBD5E1"}`,
            },
            "& td": {
              p: 1.2,
              color: darkMode ? "#CBD5E0" : "#334155",
              border: `1px solid ${darkMode ? "#4A5568" : "#E2E8F0"}`,
              verticalAlign: "top",
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {formattedMarkdown}
          </ReactMarkdown>
        </Paper>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ p: 2.5, bgcolor: darkMode ? "#1A202C" : "#FFFFFF", borderTop: `1px solid ${darkMode ? "#2D3748" : "#E2E8F0"}`, justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{
              borderColor: darkMode ? "#4A5568" : "#CBD5E1",
              color: darkMode ? "#EDF2F7" : "#334155",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              "&:hover": {
                bgcolor: darkMode ? "#2D3748" : "#F1F5F9",
                borderColor: darkMode ? "#718096" : "#94A3B8",
              },
            }}
          >
            Yazdır / PDF
          </Button>

          <Button
            variant="outlined"
            onClick={() => openDpaInNewTab(dpaDetails)}
            startIcon={<OpenInNewIcon />}
            sx={{
              borderColor: darkMode ? "#4A5568" : "#CBD5E1",
              color: darkMode ? "#EDF2F7" : "#0284C7",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              "&:hover": {
                bgcolor: darkMode ? "#2D3748" : "#F0F9FF",
                borderColor: "#0284C7",
              },
            }}
          >
            Yeni Sekmede Aç
          </Button>
        </Box>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#0F172A",
            color: "#FFFFFF",
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": { bgcolor: "#1E293B" },
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
}
